import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { publicAPI } from '../../lib/api';
import { PRACTICE_AREAS } from '../../lib/utils';
import { Scale, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { PageLoader } from '../../components/common/LoadingSpinner';

export const IntakeForm = () => {
  const [searchParams] = useSearchParams();
  const firmId = searchParams.get('firm');
  
  const [firmInfo, setFirmInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [reference, setReference] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    practice_area: '',
    description: '',
  });

  useEffect(() => {
    if (firmId) {
      fetchFirmInfo();
    } else {
      setLoading(false);
      setError('Invalid intake form link. Please contact the firm directly.');
    }
  }, [firmId]);

  const fetchFirmInfo = async () => {
    try {
      const response = await publicAPI.getFirmInfo(firmId);
      setFirmInfo(response.data);
    } catch (err) {
      setError('This intake form is no longer available. Please contact the firm directly.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const response = await publicAPI.submitIntake({
        ...formData,
        firm_id: firmId,
      });
      setReference(response.data.reference);
      setSubmitted(true);
    } catch (err) {
      setError('Failed to submit form. Please try again or contact the firm directly.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <PageLoader text="Loading..." />;
  }

  if (error && !firmInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Form Unavailable</h2>
            <p className="text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4" data-testid="intake-success">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Thank You!</h2>
            <p className="text-slate-600 mb-4">
              Your information has been submitted successfully. A member of {firmInfo?.name} will contact you shortly.
            </p>
            {reference && (
              <p className="text-sm text-slate-500">
                Reference: <span className="font-mono">{reference}</span>
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen bg-cover bg-center py-12 px-4"
      style={{ 
        backgroundImage: 'linear-gradient(to bottom, rgba(248, 250, 252, 0.95), rgba(248, 250, 252, 0.98)), url(https://images.unsplash.com/photo-1619806677949-cbae91e82cea?w=1920)'
      }}
      data-testid="intake-form-page"
    >
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Scale className="h-8 w-8 text-blue-600" />
            <span className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">
              {firmInfo?.name || 'Legal Consultation'}
            </span>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">
            Request a Consultation
          </h1>
          <p className="text-slate-600 mt-2">
            Fill out the form below and we'll get back to you within 24 hours
          </p>
        </div>

        {/* Form */}
        <Card className="shadow-xl">
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="p-4 bg-red-50 text-red-600 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="John Doe"
                    data-testid="intake-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    placeholder="john@example.com"
                    data-testid="intake-email-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    data-testid="intake-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="practice_area">Area of Law *</Label>
                  <Select
                    value={formData.practice_area}
                    onValueChange={(value) => setFormData({ ...formData, practice_area: value })}
                    required
                  >
                    <SelectTrigger data-testid="intake-practice-area-select">
                      <SelectValue placeholder="Select an area" />
                    </SelectTrigger>
                    <SelectContent>
                      {PRACTICE_AREAS.map((area) => (
                        <SelectItem key={area} value={area}>{area}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Tell us about your situation *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={5}
                  placeholder="Please provide a brief description of your legal matter..."
                  data-testid="intake-description-input"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 text-base gap-2"
                disabled={submitting}
                data-testid="intake-submit-btn"
              >
                {submitting ? (
                  <>Submitting...</>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Submit Request
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-slate-500">
                By submitting this form, you agree to be contacted by {firmInfo?.name || 'our firm'} regarding your inquiry.
                This does not establish an attorney-client relationship.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default IntakeForm;
